// utils/paymentLocal.ts
const KEY = "bbps_payment_ctx";

export type PaymentCtx = {
  refId: string;
  billerId: string;
  customerMobileNumber: string;
  customerParams: Record<string, string>;
};

type PaymentCtxWithExpiry = PaymentCtx & {
  expiresAt: number; // timestamp
};

/**
 * Save payment context with TTL (default 15 minutes)
 * Auto-expires to prevent indefinite storage of customer mobile number
 */
export const savePaymentCtx = (ctx: PaymentCtx, ttlMinutes = 15) => {
  try {
    const expiresAt = Date.now() + ttlMinutes * 60_000;
    const dataWithExpiry: PaymentCtxWithExpiry = {
      ...ctx,
      expiresAt,
    };
    localStorage.setItem(KEY, JSON.stringify(dataWithExpiry));
  } catch {
    // Silently fail if localStorage is full or blocked
    console.warn("Failed to save payment context");
  }
};

/**
 * Load payment context, auto-delete if expired
 */
export const loadPaymentCtx = (): PaymentCtx | null => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;

    const data = JSON.parse(raw) as PaymentCtxWithExpiry;

    // Check expiry
    if (data.expiresAt && Date.now() > data.expiresAt) {
      localStorage.removeItem(KEY);
      // console.info("Payment context expired and removed");
      return null;
    }

    // Return without expiresAt field
    const { expiresAt, ...ctx } = data;
    return ctx;
  } catch {
    // If parse fails or any error, clear corrupted data
    localStorage.removeItem(KEY);
    return null;
  }
};

/**
 * Clear payment context manually
 */
export const clearPaymentCtx = () => {
  try {
    localStorage.removeItem(KEY);
  } catch {
    console.warn("Failed to clear payment context");
  }
};
